/**
 * DateTile — compact month/day-range chip for course cards (find_courses
 * results, pinned reminders). Color varies by delivery mode so a glance
 * distinguishes in-person from online sessions in a scanned list.
 *
 * Date parsing deliberately avoids `new Date(dateString)`: parsing a bare
 * "YYYY-MM-DD" string is treated as UTC midnight by the spec, which shifts a
 * day backward in any negative-UTC-offset timezone (all of the Americas).
 * Splitting the string and constructing `new Date(year, month - 1, day)`
 * builds a local-time date from the same numeric parts instead, so the
 * calendar day never moves.
 */
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';

export const DATE_TILE_MODE = {
  IN_PERSON: 'in-person',
  ONLINE: 'online',
} as const;
export type DateTileMode = (typeof DATE_TILE_MODE)[keyof typeof DATE_TILE_MODE];

interface YMD {
  year: number;
  month: number;
  day: number;
}

function parseYMD(dateStr: string): YMD {
  const [year, month, day] = dateStr.split('-').map(Number);
  return { year, month, day };
}

/**
 * Pure day-range formatter — no month name, since a cross-month range (e.g.
 * "28 – 2") is intentionally shown as bare day numbers per spec. Single day
 * (no endDate, or endDate === startDate) renders just that one number.
 */
export function formatDayRange(startDate: string, endDate?: string): string {
  const start = parseYMD(startDate);
  if (!endDate || endDate === startDate) return String(start.day);
  const end = parseYMD(endDate);
  return `${start.day} – ${end.day}`;
}

function monthAbbr(dateStr: string): string {
  const { year, month, day } = parseYMD(dateStr);
  return new Date(year, month - 1, day)
    .toLocaleDateString('en-US', { month: 'short' })
    .toUpperCase();
}

const MODE_STYLES: Record<DateTileMode, { bg: string; month: string }> = {
  [DATE_TILE_MODE.IN_PERSON]: {
    bg: V3_COLORS.dateTileInPersonBg,
    month: V3_COLORS.dateTileInPersonMonth,
  },
  [DATE_TILE_MODE.ONLINE]: {
    bg: V3_COLORS.dateTileOnlineBg,
    month: V3_COLORS.dateTileOnlineMonth,
  },
};

export interface DateTileProps {
  /** "YYYY-MM-DD" */
  startDate: string;
  /** "YYYY-MM-DD" */
  endDate?: string;
  mode: DateTileMode;
  /** Tile width in px. Defaults to 66. */
  size?: number;
  className?: string;
}

export function DateTile({
  startDate,
  endDate,
  mode,
  size = 66,
  className,
}: DateTileProps) {
  const { bg, month } = MODE_STYLES[mode];

  return (
    <div
      className={cn(
        "flex flex-col items-center rounded-xl py-2 font-['Work_Sans']",
        className
      )}
      style={{ width: size, backgroundColor: bg }}
    >
      <span
        className="text-[11px] font-bold uppercase leading-none"
        style={{ color: month, letterSpacing: '0.06em' }}
      >
        {monthAbbr(startDate)}
      </span>
      <span
        className="mt-1 text-[16px] font-bold leading-none"
        style={{ color: V3_COLORS.ink }}
      >
        {formatDayRange(startDate, endDate)}
      </span>
    </div>
  );
}
